'use client'
import {
  Accordion,
  Anchor,
  Flex,
  Menu,
  ScrollArea,
  Skeleton,
  Stack,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import {
  RiArrowRightUpLine,
  RiLogoutCircleRLine,
  RiLoopRightLine,
} from '@remixicon/react'
import { Button, Text, useProfileMe } from '@repo/ui'
import { useSession } from 'next-auth/react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { FC } from 'react'
import type { OwnerDashboardKey } from './components/OwnerApps'
import { UserAvatar } from '../UserAvatar'

//@ts-ignore
import profileVerifiedIcon from '../../../assets/verified-icon.png'

import menuClasses from './Menu.module.css'
import classes from './style.module.css'

const OwnerApps = dynamic(
  () => import('./components').then((mod) => mod.OwnerApps),
  { ssr: false, loading: () => <Skeleton h={250} w='100%' radius={10} /> },
)
const RenterApps = dynamic(
  () => import('./components').then((mod) => mod.RenterApps),
  { ssr: false, loading: () => <Skeleton h={250} w='100%' radius={10} /> },
)

const LogoutModal = dynamic(() => import('../LogoutModal'), {
  ssr: false,
})
interface ProfileAvatarProps {
  height?: string
  width?: string
  currentDashboard?: OwnerDashboardKey
}

const isProfileErrorResponse = (value: unknown) =>
  Boolean(value && typeof value === 'object' && 'error' in value)

export const ProfileAvatar: FC<ProfileAvatarProps> = ({
  height = '40px',
  width = '40px',
  currentDashboard,
}) => {
  const t = useTranslations('Ui.ProfileMenu')
  const [opened, { open, close }] = useDisclosure(false)
  const { data: session, status } = useSession()

  const data = [
    {
      value: t('personas.owner'),
      content: <OwnerApps currentDashboard={currentDashboard} />,
    },
    // {
    //   value: t('personas.seller'),
    //   content: '',
    // },
    {
      value: t('personas.renter'),
      content: <RenterApps />,
    },
  ]

  const { data: userData, isLoading, isError } = useProfileMe()
  const hasAuthenticatedSession =
    status === 'authenticated' && Boolean(session?.access_token)
  const profileData = isProfileErrorResponse(userData) ? null : userData
  const sessionUser = session?.user as any
  const sessionProfile = sessionUser?.user_profile
  const firstName = profileData?.first_name || sessionProfile?.first_name
  const lastName = profileData?.last_name || sessionProfile?.last_name
  const email =
    profileData?.primary_email ||
    sessionProfile?.email ||
    sessionUser?.email ||
    sessionUser?.user_data?.email ||
    ''
  const resolvedDisplayName =
    profileData?.display_name ||
    [firstName, lastName].filter(Boolean).join(' ') ||
    sessionUser?.name ||
    email
  const displayName = resolvedDisplayName || 'User'
  const profileImageSrc =
    profileData?.profile_picture_url ||
    sessionProfile?.profile_picture_url ||
    sessionProfile?.profile_image ||
    sessionUser?.image ||
    null
  const showProfileSkeleton = isLoading && !resolvedDisplayName

  const items = data.map((item, index) => (
    <Accordion.Item
      key={item.value}
      value={item.value}
      mt={index !== 0 ? 5 : 0}
    >
      <Accordion.Control
      // disabled={item.content === ''} //TODO remove this after adding full links
      >
        {item.value}
      </Accordion.Control>
      <Accordion.Panel mt={5} p={0}>
        {item.content}
      </Accordion.Panel>
    </Accordion.Item>
  ))

  if (!hasAuthenticatedSession) {
    return null
  }

  return (
    <Menu
      shadow='md'
      width={350}
      radius='md'
      transitionProps={{ transition: 'pop-top-right', duration: 150 }}
    >
      <LogoutModal close={close} opened={opened} />

      <Menu.Target>
        <div
          style={{
            position: 'relative',
            height: height,
            width: width,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            borderRadius: '50%',
          }}
        >
          <UserAvatar
            imageSrc={profileImageSrc}
            name={displayName}
            alt={displayName}
            width={width}
            height={height}
            unoptimized
          />

          <div
            style={{
              position: 'absolute',
              bottom: '-2px',
              right: '-2px',
              backgroundColor: '#FFFFFF',
              padding: 1,
              borderRadius: '50%',
              boxShadow: '0 0 0 2px #FFFFFF',
            }}
          >
            <RiLoopRightLine size={10} color='#10182880' />
          </div>
        </div>
      </Menu.Target>

      <Menu.Dropdown>
        <ScrollArea h={{ base: 500, xl: 600 }} scrollbarSize={4}>
          <Flex
            direction='column'
            justify='center'
            align='center'
            w='100%'
            p='md'
            gap='sm'
          >
            <div className={classes.avatar}>
              <div className={classes['avatar-border']}>
                <div className={classes['avatar-background']}>
                  <UserAvatar
                    imageSrc={profileImageSrc}
                    name={displayName}
                    alt='Profile Avatar'
                    className={classes['avatar-media']}
                    unoptimized
                  />
                </div>
                <Image
                  src={profileVerifiedIcon}
                  height={24}
                  width={24}
                  alt='verified icon'
                  style={{
                    position: 'absolute',
                    bottom: '2px',
                    right: '2px',
                  }}
                />
              </div>
            </div>

            <Stack
              style={{ textAlign: 'center', alignItems: 'center' }}
              gap={5}
            >
              {showProfileSkeleton ? (
                <>
                  <Skeleton h='15px' w='60%' radius={5} />
                  <Skeleton h='15px' w='80%' radius={5} />
                </>
              ) : (
                (!isError || displayName) && (
                  <>
                    <Text c='var(--mantine-color-Neutral-6)' fz='sm' fw={600}>
                      {displayName}
                    </Text>
                    <Text c='var(--mantine-color-Neutral-5)' fz='sm'>
                      {email}
                    </Text>
                  </>
                )
              )}

              <Button
                radius='md'
                fw={500}
                fz='xs'
                bg='var(--mantine-color-Neutral-8)'
                mt='sm'
                onClick={() => window.open('/accounts-portal', '_self')}
              >
                {t('manage-account')}
              </Button>

              <Anchor href={'/global-portal'} title={t('global-portal')}>
                <span className='inline-flex font-semibold text-gray-900 items-center gap-2 text-xs leading-[160%]'>
                  {t('global-portal')}
                  <RiArrowRightUpLine size={17} color='#C2CED1' />
                </span>
              </Anchor>
            </Stack>
            <Stack
              w='100%'
              gap={0}
              p={0}
              mt='sm'
              style={{ borderRadius: '15px', overflow: 'hidden' }}
            >
              <Accordion
                variant='separated'
                defaultValue={data[0].value}
                w='100%'
                classNames={menuClasses}
              >
                {items}
              </Accordion>
              <Menu.Item
                className={classes['logout-button']}
                leftSection={<RiLogoutCircleRLine size={15} />}
                onClick={open}
              >
                {t('signout')}
              </Menu.Item>
            </Stack>
          </Flex>
        </ScrollArea>
      </Menu.Dropdown>
    </Menu>
  )
}
